export const DEFAULT_API_URL = "http://localhost:5210";
export const DEFAULT_BACKEND_PROXY_PATH = "/api/backend";

function trimTrailingSlash(value: string): string {
  return value.endsWith("/") && value.length > 1 ? value.replace(/\/+$/, "") : value;
}

function normalizeBasePath(value: string): string {
  const trimmed = value.trim();
  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return trimTrailingSlash(withLeadingSlash);
}

function isProxyEnabled(value: string | undefined): boolean {
  return ["1", "true", "yes", "on"].includes((value ?? "").trim().toLowerCase());
}

type ApiEnv = Record<string, string | undefined> &
  Partial<
    Record<
      "NEXT_PUBLIC_API_URL" | "NEXT_PUBLIC_USE_BACKEND_PROXY" | "NEXT_PUBLIC_BACKEND_PROXY_PATH",
      string
    >
  >;

export function getApiBaseUrl(env: ApiEnv = process.env): string {
  if (isProxyEnabled(env.NEXT_PUBLIC_USE_BACKEND_PROXY)) {
    return normalizeBasePath(env.NEXT_PUBLIC_BACKEND_PROXY_PATH ?? DEFAULT_BACKEND_PROXY_PATH);
  }
  return trimTrailingSlash(env.NEXT_PUBLIC_API_URL ?? DEFAULT_API_URL);
}

const USE_BACKEND_PROXY = isProxyEnabled(process.env.NEXT_PUBLIC_USE_BACKEND_PROXY);
const BASE = getApiBaseUrl({
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_USE_BACKEND_PROXY: process.env.NEXT_PUBLIC_USE_BACKEND_PROXY,
  NEXT_PUBLIC_BACKEND_PROXY_PATH: process.env.NEXT_PUBLIC_BACKEND_PROXY_PATH,
});

let token: string | null = null;

export function setToken(t: string | null) {
  token = t;
}

export function getToken(): string | null {
  if (token) return token;
  if (typeof window !== "undefined") {
    try {
      const raw = localStorage.getItem("user-store");
      if (raw) {
        const parsed = JSON.parse(raw);
        const stored = parsed?.state?.token ?? null;
        if (stored) {
          token = stored;
          return stored;
        }
      }
    } catch {}
  }
  return null;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  const t = getToken();
  if (t) headers["Authorization"] = `Bearer ${t}`;

  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    if (res.status === 401 && path !== "/api/auth/login" && typeof window !== "undefined") {
      if (window.location.pathname !== "/") {
        try {
          localStorage.setItem(
            "redirectAfterLogin",
            window.location.pathname + window.location.search,
          );
        } catch {}
        try {
          const { useUserStore } = await import("@/store/userStore");
          useUserStore.getState().logout();
        } catch {}
        window.location.assign("/");
      }
    }
    throw new ApiError(res.status, text || `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export interface ClientUser {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
}

export interface LoginBody {
  token: string;
  user: ClientUser;
}

export interface ClientColors {
  name: string;
  background: string;
  branch: string;
}

export interface ClientCoordinates {
  x: number;
  y: number;
}

export interface ClientFont {
  style: string;
  size: number | null;
  weight: string;
}

export interface ClientImage {
  src: string;
  size: number | null;
}

export interface ClientLink {
  href: string;
}

export interface ClientNodeReferenceSummary {
  id?: string;
  label: string;
  href?: string;
}

export interface ClientNodeRelatedLink {
  href: string;
  fragment?: string;
  url?: string;
  title: string;
  description: string;
}

export interface ClientNodeMetadata {
  note?: string;
  links?: Array<string | ClientNodeRelatedLink>;
  references?: ClientNodeReferenceSummary[];
  commentCount?: number;
  unresolvedCommentCount?: number;
  attachmentCount?: number;
}

export interface ClientNode {
  id: string;
  parent: string | null;
  name: string;
  coordinates: ClientCoordinates;
  colors: ClientColors;
  font: ClientFont;
  image: ClientImage;
  link: ClientLink;
  richText: string;
  metadata?: ClientNodeMetadata;
  locked: boolean;
  detached: boolean;
  isRoot: boolean;
  k: number | null;
}

export interface MapOptions {
  fontIncrement: number;
  fontMaxSize: number;
  fontMinSize: number;
}

export interface ClientMap {
  uuid: string;
  data: ClientNode[] | null;
  options: MapOptions;
  writable?: boolean;
  public: boolean;
  createdAt: string | null;
  lastModified: string | null;
  lastAccessed: string | null;
}

export interface ClientMapInfo {
  uuid: string;
  adminId: string | null;
  modificationSecret: string | null;
  rootName: string | null;
  public: boolean;
}

export interface PrivateMap {
  map: ClientMap;
  adminId: string | null;
  modificationSecret: string | null;
}

export type ShareMode = "private" | "public_readonly" | "secret_editable" | "authenticated_collaborator";

export interface MapShareSettings {
  mode: ShareMode;
  public: boolean;
  collaboratorEmails: string[];
  editableLink: string | null;
  readOnlyLink: string;
}

interface ServerMapShareSettings {
  mode: ShareMode;
  public: boolean;
  collaboratorEmails?: string[];
}

export interface CreateMapBody {
  rootNode: {
    name: string;
    colors: ClientColors;
    font: ClientFont;
    image: ClientImage;
  };
}

export interface DeleteMapBody {
  adminId: string;
}

export type ActionStatus = "open" | "in_progress" | "done";
export type ActionPriority = "low" | "medium" | "high" | "critical";

export interface ActionItem {
  id: string;
  mapId: string;
  nodeId: string;
  title: string;
  description: string;
  status: ActionStatus;
  priority: ActionPriority;
  assigneeEmail: string | null;
  dueDate?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface NodeComment {
  id: string;
  mapId: string;
  nodeId: string;
  body: string;
  mentionText: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NodeAttachment {
  id: string;
  mapId: string;
  nodeId: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAt: string;
}

export const ATTACHMENTS_MAX_FILES_PER_REQUEST = 10;

export interface NodeReference {
  id: string;
  mapId: string;
  nodeId: string;
  label: string;
  href: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RelatedLinkTitleResult {
  title: string;
  description: string;
}

export interface CreateActionBody {
  nodeId: string;
  title: string;
  description?: string;
  status?: ActionStatus;
  priority?: ActionPriority;
  assigneeEmail?: string | null;
  dueDate?: string;
}

export interface UpdateActionBody {
  title?: string;
  description?: string;
  status?: ActionStatus;
  priority?: ActionPriority;
  assigneeEmail?: string | null;
  dueDate?: string;
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginBody>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  listUsers: () => request<ClientUser[]>("/api/users"),

  listMaps: () => request<ClientMapInfo[] | null>("/api/maps"),

  createMap: (body: CreateMapBody) =>
    request<PrivateMap>("/api/maps", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getMap: (id: string, secret?: string) =>
    request<ClientMap>(`/api/maps/${id}${secret ? `?secret=${secret}` : ""}`),

  deleteMap: (id: string, adminId: string) =>
    request<void>(`/api/maps/${id}`, {
      method: "DELETE",
      body: JSON.stringify({ adminId }),
    }),

  duplicateMap: (id: string) =>
    request<PrivateMap>(`/api/maps/${id}/duplicate`, { method: "POST" }),

  setMapVisibility: (mapId: string, adminId: string, isPublic: boolean) =>
    request<void>(`/api/maps/${mapId}/visibility`, {
      method: "PATCH",
      body: JSON.stringify({ adminId, public: isPublic }),
    }),

  getMapShareSettings: async (map: ClientMap, info?: ClientMapInfo | null): Promise<MapShareSettings> => {
    const server = await request<ServerMapShareSettings>(`/api/maps/${map.uuid}/share`);
    const readOnlyLink = typeof window === "undefined" ? `/doc/${map.uuid}` : `${window.location.origin}/doc/${map.uuid}`;
    const editableLink = info?.modificationSecret
      ? `${readOnlyLink}?secret=${encodeURIComponent(info.modificationSecret)}`
      : null;
    return {
      mode: server.mode,
      public: server.public,
      collaboratorEmails: server.collaboratorEmails ?? [],
      editableLink,
      readOnlyLink,
    };
  },

  updateMapShareSettings: (mapId: string, adminId: string, mode: ShareMode, collaboratorEmails?: string[]) =>
    request<ServerMapShareSettings>(`/api/maps/${mapId}/share`, {
      method: "PATCH",
      body: JSON.stringify({
        adminId,
        mode,
        public: mode === "public_readonly",
        collaboratorEmails: collaboratorEmails ?? [],
      }),
    }),

  // Action items
  listActions: (mapId: string) =>
    request<ActionItem[]>(`/api/maps/${mapId}/actions`),

  createAction: (mapId: string, body: CreateActionBody) =>
    request<ActionItem>(`/api/maps/${mapId}/actions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getAction: (mapId: string, actionId: string) =>
    request<ActionItem>(`/api/maps/${mapId}/actions/${actionId}`),

  updateAction: (mapId: string, actionId: string, body: UpdateActionBody) =>
    request<ActionItem>(`/api/maps/${mapId}/actions/${actionId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteAction: (mapId: string, actionId: string) =>
    request<void>(`/api/maps/${mapId}/actions/${actionId}`, {
      method: "DELETE",
    }),

  listMyActions: () => request<ActionItem[]>("/api/actions/mine"),

  // Node CRUD
  createNode: (mapId: string, secret: string, node: ClientNode) =>
    request<ClientNode>(`/api/maps/${mapId}/nodes?secret=${encodeURIComponent(secret)}`, {
      method: "POST",
      body: JSON.stringify(node),
    }),

  updateNode: (mapId: string, secret: string, nodeId: string, node: ClientNode) =>
    request<ClientNode>(`/api/maps/${mapId}/nodes/${nodeId}?secret=${encodeURIComponent(secret)}`, {
      method: "PUT",
      body: JSON.stringify(node),
    }),

  deleteNode: (mapId: string, secret: string, nodeId: string) =>
    request<void>(`/api/maps/${mapId}/nodes/${nodeId}?secret=${encodeURIComponent(secret)}`, {
      method: "DELETE",
    }),

  listNodeComments: (mapId: string, nodeId: string) =>
    request<NodeComment[]>(`/api/maps/${mapId}/nodes/${nodeId}/comments`),

  createNodeComment: (mapId: string, nodeId: string, secret: string, body: { body: string; mentionText?: string }) =>
    request<NodeComment>(`/api/maps/${mapId}/nodes/${nodeId}/comments?secret=${encodeURIComponent(secret)}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  resolveNodeComment: (mapId: string, nodeId: string, commentId: string, secret: string) =>
    request<NodeComment>(`/api/maps/${mapId}/nodes/${nodeId}/comments/${commentId}/resolve?secret=${encodeURIComponent(secret)}`, {
      method: "PATCH",
    }),

  listNodeReferences: (mapId: string, nodeId: string) =>
    request<NodeReference[]>(`/api/maps/${mapId}/nodes/${nodeId}/references`),

  createNodeReference: (mapId: string, nodeId: string, secret: string, body: { label: string; href?: string }) =>
    request<NodeReference>(`/api/maps/${mapId}/nodes/${nodeId}/references?secret=${encodeURIComponent(secret)}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteNodeReference: (mapId: string, nodeId: string, referenceId: string, secret: string) =>
    request<void>(`/api/maps/${mapId}/nodes/${nodeId}/references/${referenceId}?secret=${encodeURIComponent(secret)}`, {
      method: "DELETE",
    }),

  discoverRelatedLinkTitle: (mapId: string, href: string, secret?: string) =>
    request<RelatedLinkTitleResult>(
      `/api/maps/${mapId}/related-link-title${secret ? `?secret=${encodeURIComponent(secret)}` : ""}`,
      {
        method: "POST",
        body: JSON.stringify({ href }),
      },
    ),

  // Node attachments
  listNodeAttachments: (mapId: string, nodeId: string, secret?: string | null) =>
    request<NodeAttachment[]>(
      `/api/maps/${mapId}/nodes/${nodeId}/attachments${secret ? `?secret=${encodeURIComponent(secret)}` : ""}`,
    ),

  uploadNodeAttachment: async (
    mapId: string,
    nodeId: string,
    secret: string,
    file: File,
  ): Promise<NodeAttachment> => {
    const form = new FormData();
    form.append("file", file, file.name);
    const headers: Record<string, string> = {};
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
    const res = await fetch(
      `${BASE}/api/maps/${mapId}/nodes/${nodeId}/attachments?secret=${encodeURIComponent(secret)}`,
      { method: "POST", body: form, credentials: "include", headers },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new ApiError(res.status, text || `${res.status} ${res.statusText}`);
    }
    const list = (await res.json()) as NodeAttachment[];
    return list[0];
  },

  deleteNodeAttachment: (mapId: string, attachmentId: string, secret: string) =>
    request<void>(
      `/api/maps/${mapId}/attachments/${attachmentId}?secret=${encodeURIComponent(secret)}`,
      { method: "DELETE" },
    ),

  // Resolves to a blob URL the caller can use to trigger a browser download or preview.
  downloadNodeAttachment: async (
    mapId: string,
    attachmentId: string,
    secret?: string | null,
  ): Promise<{ blob: Blob; filename: string }> => {
    const headers: Record<string, string> = {};
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
    const url = `${BASE}/api/maps/${mapId}/attachments/${attachmentId}${secret ? `?secret=${encodeURIComponent(secret)}` : ""}`;
    const res = await fetch(url, { credentials: "include", headers });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new ApiError(res.status, text || `${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const match = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(disposition);
    const filename = decodeURIComponent(match?.[1] ?? match?.[2] ?? "attachment");
    return { blob, filename };
  },
};
